import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { jwtDecode } from "jwt-decode";
import {
  Button,
  Typography,
  Box,
  Chip,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
  IconButton,
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DynamicTable from "../TabComponents/DynamicTable";
import {
  fetchCourses,
  uploadDocument,
  fetchStudentDocuments,
  deleteDocument,
} from "../../../../../utils/Apis";

const UploadTab = () => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const userData = useSelector((state) => state.user?.data);
  const { id, firstName, role, branchId } = jwtDecode(token);
  const allCourses = useSelector((state) => state.data.courses?.courses || []);
  const documentsFromState = useSelector((state) => state.data.documents || []);

  const [title, setTitle] = useState("");
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [localDocuments, setLocalDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");

  const courseOptions =
    role === "admin"
      ? allCourses
      : userData?.studentDetails?.courses?.map((course) => course.course) || [];

  const loadDocuments = async () => {
    let docs = documentsFromState;

    // If Redux state is empty, fetch from backend
    if (!docs || docs.length === 0) {
      try {
        const res = await dispatch(fetchStudentDocuments()).unwrap();
        docs = res?.documents || [];
      } catch (error) {
        console.error("Failed to fetch documents:", error);
        docs = [];
      }
    }

    // Filter based on role
    if (role === "admin") {
      setLocalDocuments(docs);
    } else {
      const studentCourseIds = courseOptions; // array of string IDs

      const filteredDocs = docs.filter(
        (doc) =>
          Array.isArray(doc.courseIds) &&
          doc.courseIds.some((course) =>
            studentCourseIds.includes(String(course._id))
          )
      );

      setLocalDocuments(filteredDocs);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== "application/pdf")
      return alert("Only PDF files allowed.");
    if (file.size > 5 * 1024 * 1024) return alert("Max file size is 5MB.");
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !title || selectedCourses.length === 0) {
      alert("Please fill all fields.");
      return;
    }

    const formData = new FormData();
    formData.append("notes", selectedFile);
    formData.append("title", title);
    formData.append("uploadedBy", id);
    formData.append("uploaderName", firstName);
    formData.append("branchId", branchId);
    formData.append("role", role);
    selectedCourses.forEach((courseId) =>
      formData.append("courseIds[]", courseId)
    );

    setUploading(true);
    try {
      await dispatch(uploadDocument(formData)).unwrap();
      setSelectedFile(null);
      setTitle("");
      setSelectedCourses([]);
      await loadDocuments(); // Reload after upload
    } catch (err) {
      alert(err.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
    if (role === "admin" && allCourses.length === 0) {
      dispatch(fetchCourses());
    }
  }, []);

  const filteredDocs = localDocuments.filter((doc) =>
    doc.title.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    { label: "Title", key: "title", sortable: true },
    {
      label: "Courses",
      key: "courseIds",
      render: (row) =>
        row.courseIds?.map((c) => (
          <Chip key={c._id} label={c.name} size="small" sx={{ m: 0.5 }} />
        )),
    },
    {
      label: "Uploaded By",
      key: "uploadedBy.name",
      render: (row) => row.uploadedBy?.name || "N/A",
      sortable: true,
    },
    {
      label: "Uploaded At",
      key: "uploadedAt",
      render: (row) =>
        new Date(row.uploadedAt).toLocaleString("en-IN", {
          dateStyle: "medium",
          timeStyle: "short",
        }),
      sortable: true,
    },
    ...(role === "admin"
      ? [
          {
            label: "Actions",
            key: "actions",
            render: (row) => (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row click event
                  handleDelete(row._id);
                }}
                color="error"
              >
                <DeleteForeverIcon />
              </IconButton>
            ),
          },
        ]
      : []),
  ];

  const handleRowClick = (row) => {
    const filePath = row.filePath; // Assuming the row has a filePath field
    if (filePath) {
      // Open the file in a new tab
      window.open(filePath, "_blank");
    }
  };

  const handleDelete = async (docId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this document?"
    );
    if (!confirmDelete) return;

    try {
      await dispatch(deleteDocument(docId)).unwrap();
      await loadDocuments(); // Refresh list
    } catch (err) {
      alert(err.message || "Failed to delete document");
    }
  };

  return (
    <div className="p-4 space-y-6">
      {role === "admin" && (
        <div className="bg-gray-50 border border-dashed border-gray-300 p-6 rounded-lg space-y-4">
          <Typography variant="h6" fontWeight="bold">
            Upload Notes
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <FormControl>
              <InputLabel sx={{ minWidth: 300 }}>Select Courses</InputLabel>
              <Select
                sx={{ minWidth: 300, maxWidth: 600 }}
                multiple
                value={selectedCourses}
                onChange={(e) => setSelectedCourses(e.target.value)}
                renderValue={(selected) => (
                  <div style={{ display: "flex", flexWrap: "wrap" }}>
                    {selected.map((id) => {
                      const course = courseOptions.find((c) => c._id === id);
                      return course ? (
                        <Chip
                          key={course._id}
                          label={course.name}
                          size="small"
                          sx={{ m: 0.5 }}
                        />
                      ) : null;
                    })}
                  </div>
                )}
              >
                {courseOptions.map((course) => (
                  <MenuItem key={course._id} value={course._id}>
                    {course.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <label
              htmlFor="upload-input"
              className="border border-blue-500 hover:bg-blue-100 text-blue-600 px-4 py-2 rounded cursor-pointer inline-flex items-center gap-2"
            >
              <CloudUploadIcon />
              {selectedFile ? selectedFile.name : "Browse PDF"}
            </label>
            <input
              id="upload-input"
              type="file"
              hidden
              name="notes"
              accept=".pdf"
              onChange={handleFileChange}
              maxWidth={300}
            />

            <Button
              variant="contained"
              disabled={uploading || !selectedFile}
              onClick={handleUpload}
              startIcon={uploading && <CircularProgress size={16} />}
            >
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </Box>
        </div>
      )}

      <div className="flex justify-between items-center flex-wrap gap-4">
        <Typography variant="h6">
          {role === "admin" ? "All Documents" : "Your Course Documents"}
        </Typography>
        <input
          type="text"
          placeholder="Search documents..."
          className="border px-3 py-1 rounded w-full sm:w-auto"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <DynamicTable
        columns={columns}
        rows={filteredDocs}
        total={filteredDocs.length}
        page={1}
        perPage={filteredDocs.length}
        onPageChange={() => {}}
        onPerPageChange={() => {}}
        onSort={() => {}}
        sortField=""
        sortOrder="asc"
        loading={false}
        onRowClick={handleRowClick}
      />
    </div>
  );
};

export default UploadTab;
